<script setup lang="ts">
import { ref } from "vue";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";
import { TaskHistoryEntry } from "@/types/v2";

// Get equipment functions
const { getEquipmentHistory, formatTimestamp } = useEquipment();
const taskHistory = useTaskHistory();

// State for editing history justifications
const editingHistoryEntry = ref<TaskHistoryEntry | null>(null);
const editedJustification = ref('');

// Start editing a history entry's justification
const startEditingHistory = (entry: TaskHistoryEntry) => {
  editingHistoryEntry.value = entry;
  editedJustification.value = entry.justification || '';
};

// Cancel editing history
const cancelEditingHistory = () => {
  editingHistoryEntry.value = null;
  editedJustification.value = '';
};

const saveHistoryJustification = (entry: TaskHistoryEntry) => {
  const history = taskHistory.getHistory();
  const entryIndex = history.findIndex(e => e.id === entry.id);

  if (entryIndex === -1) return;

  history[entryIndex].justification = editedJustification.value;

  // Reset editing state
  editingHistoryEntry.value = null;
  editedJustification.value = '';
};

</script>

<template>
  <div>
    <h2 class="text-lg font-semibold mb-4">Equipment Change History</h2>

    <div v-if="getEquipmentHistory.length > 0" class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
        <tr>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Justification</th>
        </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
        <tr v-for="entry in getEquipmentHistory" :key="entry.id" class="hover:bg-gray-50">
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatTimestamp(entry.timestamp) }}</td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900">{{ entry.userName }}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm text-gray-900">{{ entry.fieldName }}</div>
            <div class="text-xs text-gray-500">{{ entry.context }}</div>
          </td>
          <td class="px-6 py-4">
            <div class="flex flex-col space-y-1">
              <div v-if="entry.oldValue !== null" class="text-xs bg-red-50 p-1 rounded border border-red-100">
                <span class="font-semibold text-red-700">Old:</span>
                <span class="text-gray-800">{{ typeof entry.oldValue === 'object' ? JSON.stringify(entry.oldValue) : entry.oldValue }}</span>
              </div>
              <div v-if="entry.newValue !== null" class="text-xs bg-green-50 p-1 rounded border border-green-100">
                <span class="font-semibold text-green-700">New:</span>
                <span class="text-gray-800">{{ typeof entry.newValue === 'object' ? JSON.stringify(entry.newValue) : entry.newValue }}</span>
              </div>
            </div>
          </td>
          <td class="px-6 py-4 text-sm">
            <!-- Justification in edit mode -->
            <div v-if="editingHistoryEntry && editingHistoryEntry.id === entry.id" class="flex flex-col space-y-2">
                <textarea
                    v-model="editedJustification"
                    class="w-full border border-gray-300 rounded p-2 text-sm"
                    rows="3"
                    placeholder="Enter justification..."></textarea>
              <div class="flex justify-end space-x-2">
                <button
                    @click="saveHistoryJustification(entry)"
                    class="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">
                  Save
                </button>
                <button
                    @click="cancelEditingHistory"
                    class="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700">
                  Cancel
                </button>
              </div>
            </div>

            <!-- Justification display mode -->
            <div v-else class="flex justify-between items-start">
              <div class="text-gray-500 max-w-xs">
                {{ entry.justification || '-' }}
              </div>
              <button
                  @click="startEditingHistory(entry)"
                  class="ml-2 px-2 py-1 bg-gray-100 text-xs rounded hover:bg-gray-200"
                  title="Edit justification">
                Edit
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="p-4 text-center bg-gray-50 border rounded">
      <p class="text-gray-500">No equipment change history available.</p>
    </div>
  </div>
</template>